/**
 * Embeddable loader. Runs on customer websites, so it is dependency-free and small.
 *
 * Responsibilities:
 *  - boot the visitor from the host page (so the server sees the real `Origin`)
 *  - keep the visitor token in first-party storage on the host site
 *  - render the launcher, unread badge and message previews (Shadow DOM)
 *  - run automatic messages (triggers) and show them as previews
 *  - host the messenger iframe and bridge it with postMessage
 *  - expose `window.ChatWidget(command, ...args)`
 */
import { LAUNCHER_STYLES, populateLauncher } from '@shared/launcher';
import { PREVIEW_STYLES, populatePreview, refreshPreviewTimes } from '@shared/preview-bubble';
import type { MatchContext, VisitorTrigger } from '@shared/triggers';
import type { Message } from '@shared/types';
import {
    envelope,
    isEnvelope,
    type BootPayload,
    type CobrowseSessionPayload,
    type FromFrame,
    type ToFrame,
    type TriggerOpener,
} from '@shared/widget-protocol';

import { TriggerRunner, type SessionStore } from './trigger-runner';

interface UserData {
    user_id?: string | number;
    user_hash?: string;
    email?: string;
    name?: string;
    phone?: string;
    avatar_url?: string;
    attributes?: Record<string, unknown>;
}

interface WidgetSettingsInput {
    widget_key?: string;
    user?: UserData;
    hide_launcher?: boolean;
    /** Dashboard canvas: run the actual embed with sample data and no visitor tracking. */
    preview?: BootPayload;
}

type Command =
    | 'show'
    | 'hide'
    | 'toggle'
    | 'showNewMessage'
    | 'identify'
    | 'update'
    | 'shutdown'
    | 'onUnreadCountChange'
    | 'onShow'
    | 'onHide';

type Callback = (...args: unknown[]) => void;
type QueuedCall = [Command, ...unknown[]];

interface StubFunction {
    (command: Command, ...args: unknown[]): void;
    q?: QueuedCall[];
}

interface CobrowseRecorderApi {
    start(emit: (event: unknown) => void): void;
    stop(): void;
    snapshot(): void;
    pointer(x: number | null, y: number | null, name?: string): void;
    highlight(x: number, y: number): void;
}

declare global {
    interface Window {
        __chatCobrowse?: CobrowseRecorderApi;
        ChatWidgetSettings?: WidgetSettingsInput;
        ChatWidget?: StubFunction;
        __chatWidgetLoaded?: boolean;
    }
}

const script = document.currentScript as HTMLScriptElement | null;
const ORIGIN = script ? new URL(script.src).origin : window.location.origin;
const API = `${ORIGIN}/api/widget/v1`;
const HEARTBEAT_MS = 20_000;
/** Background tabs still keep the visitor "online", just with fewer pings. */
const HIDDEN_HEARTBEAT_MS = 40_000;
/** Interaction within this window counts as "active". */
const ACTIVE_WINDOW_MS = 30_000;
const MIN_FORCED_GAP_MS = 3_000;

const storage = {
    get(key: string): string | null {
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set(key: string, value: string | null) {
        try {
            if (value === null) window.localStorage.removeItem(key);
            else window.localStorage.setItem(key, value);
        } catch {
            /* storage unavailable (privacy mode); the visitor will be anonymous per page load */
        }
    },
};

/** Browsers cap cookie lifetimes at 400 days; each visit renews it. */
const VISITOR_COOKIE_MAX_AGE = 400 * 24 * 60 * 60;

/** First-party cookie on the host site that remembers the visitor. */
const cookie = {
    get(name: string): string | null {
        try {
            const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
            return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
        } catch {
            return null;
        }
    },
    set(name: string, value: string | null) {
        try {
            const secure = window.location.protocol === 'https:' ? '; Secure' : '';
            document.cookie =
                value === null
                    ? `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`
                    : `${name}=${encodeURIComponent(value)}; Max-Age=${VISITOR_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
        } catch {
            /* cookies unavailable (sandboxed page); localStorage still remembers the visitor */
        }
    },
};

/** Per-tab browsing session: what triggers fired, when the visit started. */
const session: SessionStore = {
    get(key) {
        try {
            return window.sessionStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set(key, value) {
        try {
            if (value === null) window.sessionStorage.removeItem(key);
            else window.sessionStorage.setItem(key, value);
        } catch {
            /* storage unavailable: triggers may repeat on the next page */
        }
    },
};

/** A trigger message showing above the launcher (kept across pages until clicked or dismissed). */
interface ActiveTrigger {
    trigger: VisitorTrigger;
    shown_at: string;
}

function currentPage() {
    return { url: window.location.href, title: document.title, referrer: document.referrer || null };
}

/** Below this width the open messenger fills the screen (matches the media query in STYLES). */
const FULLSCREEN_QUERY = '(max-width: 450px)';

const STYLES = `
:host { all: initial; }
* { box-sizing: border-box; font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; }
.root { position: fixed; bottom: 20px; z-index: 2147483000; display: flex; flex-direction: column; gap: 16px; }
.root.right { right: 20px; align-items: flex-end; }
.root.left { left: 20px; align-items: flex-start; }
${LAUNCHER_STYLES}
.badge { position: absolute; top: -2px; right: -2px; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: #ef4444; color: #fff; font-size: 12px; font-weight: 600; line-height: 20px; text-align: center;
  box-shadow: 0 0 0 2px #fff; }
.badge[hidden] { display: none; }
.frame { position: fixed; bottom: 96px; width: 400px; height: min(704px, calc(100vh - 116px)); border-radius: 18px;
  overflow: hidden; background: #fff; box-shadow: 0 12px 48px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.06);
  opacity: 0; transform: translateY(16px) scale(.98); transform-origin: bottom right; pointer-events: none;
  transition: opacity .22s ease, transform .28s cubic-bezier(.2,.8,.2,1); }
.root.left .frame { transform-origin: bottom left; }
.root.right .frame { right: 20px; }
.root.left .frame { left: 20px; }
.open .frame { opacity: 1; transform: none; pointer-events: auto; }
.frame iframe { width: 100%; height: 100%; border: 0; display: block; color-scheme: light; }
${PREVIEW_STYLES}
@media ${FULLSCREEN_QUERY} {
  .open .frame, .frame { inset: 0; width: 100%; height: 100%; border-radius: 0; bottom: 0; }
  .open .launcher { display: none; }
}
@media (prefers-reduced-motion: reduce) { .frame, .launcher, .launcher .icon { transition: none; animation: none; } }
`;

class Widget {
    private key: string;
    private storageKey: string;
    private cookieName: string;
    private boot: BootPayload | null = null;
    private isOpen = false;
    private unread = 0;
    /** Unread replies, as counted by the messenger (a showing trigger message adds one). */
    private serverUnread = 0;
    private triggers: TriggerRunner | null = null;
    private activeTrigger: ActiveTrigger | null = null;
    /** The visitor opened the messenger on this page: no automatic messages until the next boot. */
    private engaged = false;
    private previewClock: number | undefined;
    private iframe: HTMLIFrameElement | null = null;
    private frameReady = false;
    private outbox: ToFrame[] = [];
    private listeners: Record<string, Set<Callback>> = {};
    private host: HTMLDivElement | null = null;
    /** The host page's overflow before a full-screen messenger locked its scrolling. */
    private lockedOverflow: string | null = null;
    private els!: {
        root: HTMLDivElement;
        launcher: HTMLButtonElement;
        badge: HTMLSpanElement;
        frame: HTMLDivElement;
        previews: HTMLDivElement;
    };
    private heartbeatTimer: number | undefined;
    private lastUrl = '';
    private lastHeartbeat = Date.now();
    private lastInteraction = Date.now();

    constructor(private input: WidgetSettingsInput) {
        this.key = input.widget_key!;
        this.storageKey = `chatwidget:${this.key}:token`;
        this.cookieName = `chatwidget_${this.key.replace(/[^\w-]/g, '_')}`;
    }

    /** The visitor token: from the long-lived cookie, else localStorage (older installs, blocked cookies). */
    private visitorToken() {
        return cookie.get(this.cookieName) ?? storage.get(this.storageKey);
    }

    /** Keep the visitor for 400 days from their latest visit, in both places. */
    private rememberVisitor(token: string | null) {
        cookie.set(this.cookieName, token);
        storage.set(this.storageKey, token);
    }

    private sessionKey(name: string) {
        return `chatwidget:${this.key}:${name}`;
    }

    async start() {
        if (this.input.preview?.preview) {
            this.boot = this.input.preview;
            this.render();
            this.setUnread(0);
            this.mountFrame();
            this.startTriggers();
            return;
        }
        try {
            this.boot = await this.request<BootPayload>('POST', '/boot', {
                widget_key: this.key,
                token: this.visitorToken(),
                page: currentPage(),
                locale: navigator.language,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                user: this.input.user ?? null,
            });
        } catch (error) {
            console.warn('[ChatWidget] Could not start:', (error as Error).message);
            return;
        }

        if (this.boot.identity_error) console.warn('[ChatWidget] identify:', this.boot.identity_error);

        this.rememberVisitor(this.boot.token);
        this.lastUrl = window.location.href;
        this.render();
        this.setUnread(this.boot.unread_count);
        this.trackNavigation();
        this.startHeartbeat();
        this.startTriggers();

        // An active co-browsing session needs the messenger connection right away.
        if (this.boot.cobrowse) {
            this.mountFrame();
        } else {
            const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1200));
            idle(() => this.mountFrame());
        }
    }

    // ---- Public API ---------------------------------------------------------

    command(name: Command, ...args: unknown[]) {
        if (this.boot?.preview && ['identify', 'update', 'shutdown'].includes(name)) return;
        switch (name) {
            case 'show':
                return this.setOpen(true);
            case 'hide':
                return this.setOpen(false);
            case 'toggle':
                return this.setOpen(!this.isOpen);
            case 'showNewMessage':
                return this.setOpen(true, typeof args[0] === 'string' ? args[0] : '');
            case 'identify':
                return void this.identify((args[0] ?? {}) as UserData);
            case 'update':
                return void this.identify({ attributes: (args[0] ?? {}) as Record<string, unknown> });
            case 'shutdown':
                return void this.shutdown();
            case 'onUnreadCountChange':
                return this.on('unread', args[0] as Callback);
            case 'onShow':
                return this.on('show', args[0] as Callback);
            case 'onHide':
                return this.on('hide', args[0] as Callback);
            default:
                console.warn(`[ChatWidget] Unknown command "${String(name)}"`);
        }
    }

    // ---- Rendering ------------------------------------------------------------

    private render() {
        const settings = this.boot!.workspace.settings;

        this.host = document.createElement('div');
        this.host.id = 'chat-widget-host';
        this.host.style.setProperty('--brand', settings.color);
        this.host.style.setProperty('--brand-fg', '#ffffff');
        const shadow = this.host.attachShadow({ mode: 'open' });

        shadow.innerHTML = `
            <style>${STYLES}</style>
            <div class="root ${settings.position === 'left' ? 'left' : 'right'}">
                <div class="previews" aria-live="polite"></div>
                <div class="frame" role="dialog" aria-label="Chat messenger"></div>
                <button class="launcher" type="button" aria-label="Open chat" aria-expanded="false"${this.input.hide_launcher ? ' hidden' : ''}>
                    <span class="badge" hidden></span>
                </button>
            </div>`;

        this.els = {
            root: shadow.querySelector('.root')!,
            launcher: shadow.querySelector('.launcher')!,
            badge: shadow.querySelector('.badge')!,
            frame: shadow.querySelector('.frame')!,
            previews: shadow.querySelector('.previews')!,
        };

        populateLauncher(this.els.launcher, settings.launcher);
        this.els.launcher.addEventListener('click', () => this.setOpen(!this.isOpen));
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) this.setOpen(false);
        });

        window.visualViewport?.addEventListener('resize', this.fitToViewport);
        window.visualViewport?.addEventListener('scroll', this.fitToViewport);

        document.body.appendChild(this.host);
    }

    /**
     * Full screen on phones: keep the frame inside the visual viewport, so the
     * on-screen keyboard can't cover the composer (iOS doesn't shrink the
     * layout viewport for it), and stop the page behind from scrolling.
     */
    private fitToViewport = () => {
        const frame = this.els?.frame;
        if (!frame) return;
        const viewport = window.visualViewport;
        const fullscreen = this.isOpen && window.matchMedia(FULLSCREEN_QUERY).matches;

        if (fullscreen && viewport) {
            frame.style.top = `${viewport.offsetTop}px`;
            frame.style.height = `${viewport.height}px`;
        } else {
            frame.style.removeProperty('top');
            frame.style.removeProperty('height');
        }
        this.lockPageScroll(fullscreen);
    };

    private lockPageScroll(lock: boolean) {
        const root = document.documentElement;
        if (lock && this.lockedOverflow === null) {
            this.lockedOverflow = root.style.overflow;
            root.style.overflow = 'hidden';
        } else if (!lock && this.lockedOverflow !== null) {
            root.style.overflow = this.lockedOverflow;
            this.lockedOverflow = null;
        }
    }

    private mountFrame() {
        if (this.iframe || !this.boot) return;

        this.iframe = document.createElement('iframe');
        this.iframe.title = `Chat with ${this.boot.workspace.name}`;
        this.iframe.allow = 'clipboard-write';
        this.iframe.src = `${ORIGIN}/widget/frame`;
        this.els.frame.appendChild(this.iframe);

        window.addEventListener('message', this.onFrameMessage);
    }

    private setOpen(open: boolean, prefill?: string) {
        if (!this.boot) return;
        this.mountFrame();
        this.isOpen = open;
        this.els.root.classList.toggle('open', open);
        this.els.launcher.setAttribute('aria-expanded', String(open));
        this.els.launcher.setAttribute('aria-label', open ? 'Close chat' : 'Open chat');
        this.fitToViewport();

        if (open) {
            this.engaged = true;
            const trigger = prefill === undefined ? this.takeActiveTrigger('clicked') : undefined;
            this.clearPreviews();
            this.post({ type: 'open', prefill, trigger });
            this.emit('show');
        } else {
            this.post({ type: 'close' });
            this.emit('hide');
        }
    }

    private setUnread(count: number) {
        this.serverUnread = count;
        this.renderUnread();
    }

    private renderUnread() {
        const count = this.serverUnread + (this.activeTrigger ? 1 : 0);
        this.unread = count;
        this.els.badge.hidden = count === 0;
        this.els.badge.textContent = count > 9 ? '9+' : String(count);
        this.emit('unread', count);
    }

    private showPreview(message: Message) {
        if (this.isOpen) return;

        const card = document.createElement('div');
        card.dataset.id = message.id;
        populatePreview(card, {
            name: message.sender.name ?? this.boot!.workspace.name,
            avatarUrl: message.sender.avatar_url,
            body: message.body ?? '',
            at: message.created_at,
        });

        card.addEventListener('click', (event) => {
            if ((event.target as HTMLElement).closest('.dismiss')) {
                card.remove();
                return;
            }
            this.setOpen(true);
        });
        card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && event.target === card) this.setOpen(true);
        });

        this.addPreview(card);
    }

    private addPreview(card: HTMLElement) {
        this.els.previews.appendChild(card);
        while (this.els.previews.children.length > 3) this.els.previews.firstElementChild?.remove();

        this.previewClock ??= window.setInterval(() => refreshPreviewTimes(this.els.previews), 60_000);
    }

    private clearPreviews() {
        this.els.previews.replaceChildren();
        window.clearInterval(this.previewClock);
        this.previewClock = undefined;
    }

    // ---- Automatic messages (triggers) ------------------------------------------

    private startTriggers() {
        const boot = this.boot!;
        const test = boot.preview ? boot.trigger_test : null;
        if (boot.preview && !test) return;

        if (!session.get(this.sessionKey('visit_started')))
            session.set(this.sessionKey('visit_started'), String(Date.now()));

        // A trigger message still waiting for the visitor (from an earlier page) stays up.
        if (!test) {
            try {
                const stored = JSON.parse(
                    session.get(this.sessionKey('active_trigger')) ?? 'null',
                ) as ActiveTrigger | null;
                if (stored?.trigger?.id) this.showTrigger(stored);
            } catch {
                session.set(this.sessionKey('active_trigger'), null);
            }
        }

        this.triggers = new TriggerRunner({
            triggers: boot.triggers ?? [],
            context: () => this.triggerContext(),
            canFire: () =>
                !this.isOpen &&
                !this.activeTrigger &&
                (!!test || (!this.engaged && !boot.trigger_context?.open_conversation)),
            fire: (trigger) => {
                this.showTrigger({ trigger, shown_at: new Date().toISOString() });
                this.recordTrigger(trigger.id, 'shown');
            },
            session,
            sessionKey: this.sessionKey('fired_triggers'),
            test: !!test,
        });
        this.triggers.evaluate();
    }

    private triggerContext(): MatchContext {
        const boot = this.boot!;
        const started = Number(session.get(this.sessionKey('visit_started'))) || Date.now();
        const mobile = window.matchMedia?.('(pointer: coarse)').matches && window.innerWidth < 1024;

        return {
            url: boot.trigger_test?.url ?? window.location.href,
            referrer: document.referrer,
            device: mobile ? 'mobile' : 'desktop',
            secondsOnSite: (Date.now() - started) / 1000,
            returning: boot.trigger_context?.returning ?? false,
            identified: boot.contact.is_identified,
            emailKnown: !!boot.contact.email,
            attributes: boot.trigger_context?.attributes ?? {},
            officeHoursOpen: boot.workspace.office_hours_open,
        };
    }

    private showTrigger(active: ActiveTrigger) {
        const { trigger } = active;
        this.activeTrigger = active;
        if (!this.boot?.preview) session.set(this.sessionKey('active_trigger'), JSON.stringify(active));

        const card = document.createElement('div');
        card.dataset.trigger = trigger.id;
        populatePreview(card, {
            name: trigger.sender?.name ?? this.boot!.workspace.name,
            avatarUrl: trigger.sender?.avatar_url,
            body: trigger.message,
            at: active.shown_at,
        });

        card.addEventListener('click', (event) => {
            if ((event.target as HTMLElement).closest('.dismiss')) {
                this.takeActiveTrigger('dismissed');
                card.remove();
                return;
            }
            this.setOpen(true);
        });
        card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && event.target === card) this.setOpen(true);
        });

        this.addPreview(card);
        this.renderUnread();
    }

    /** The visitor clicked or dismissed the trigger message: it's done. */
    private takeActiveTrigger(outcome: 'clicked' | 'dismissed'): TriggerOpener | undefined {
        const active = this.activeTrigger;
        if (!active) return undefined;

        this.activeTrigger = null;
        session.set(this.sessionKey('active_trigger'), null);
        this.els.previews.querySelector(`[data-trigger="${active.trigger.id}"]`)?.remove();
        this.renderUnread();
        this.recordTrigger(active.trigger.id, outcome);

        return outcome === 'clicked'
            ? {
                  trigger_id: active.trigger.id,
                  message: active.trigger.message,
                  sender: active.trigger.sender,
                  shown_at: active.shown_at,
              }
            : undefined;
    }

    private recordTrigger(id: string, type: 'shown' | 'clicked' | 'dismissed') {
        if (this.boot?.preview) return;
        this.request('POST', `/triggers/${id}/events`, { type }).catch(() => undefined);
    }

    // ---- Frame bridge ---------------------------------------------------------

    private onFrameMessage = (event: MessageEvent) => {
        if (event.origin !== ORIGIN || event.source !== this.iframe?.contentWindow) return;
        if (!isEnvelope<FromFrame>(event.data)) return;

        const message = event.data.payload;

        switch (message.type) {
            case 'ready':
                this.frameReady = true;
                this.post({ type: 'init', boot: this.boot!, open: this.isOpen }, true);
                this.outbox.splice(0).forEach((m) => this.post(m));
                break;
            case 'request-close':
                this.setOpen(false);
                break;
            case 'unread':
                this.setUnread(message.count);
                break;
            case 'preview':
                this.showPreview(message.message);
                break;
            case 'clear-previews':
                this.clearPreviews();
                break;
            case 'activity':
                this.onInteraction();
                break;
            case 'cobrowse-start':
                void this.loadRecorder().then((recorder) => recorder.start((event) => this.queueCobrowseEvent(event)));
                break;
            case 'cobrowse-stop':
                window.__chatCobrowse?.stop();
                this.cobrowseBuffer = [];
                break;
            case 'cobrowse-snapshot':
                window.__chatCobrowse?.snapshot();
                break;
            case 'cobrowse-pointer':
                window.__chatCobrowse?.pointer(message.x, message.y, message.name);
                break;
            case 'cobrowse-highlight':
                window.__chatCobrowse?.highlight(message.x, message.y);
                break;
        }
    };

    private post(message: ToFrame, force = false) {
        if (!this.iframe?.contentWindow || (!this.frameReady && !force)) {
            if (message.type !== 'init') this.outbox.push(message);
            return;
        }
        this.iframe.contentWindow.postMessage(envelope(message), ORIGIN);
    }

    // ---- Server calls ---------------------------------------------------------

    private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
        if (this.boot?.preview) throw new Error('Server calls are disabled in preview mode.');
        const token = this.boot?.token;
        const response = await fetch(`${API}${path}`, {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...(token && path !== '/boot' ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body === undefined ? undefined : JSON.stringify(body),
            keepalive: path === '/heartbeat',
        });

        if (response.status === 204) return undefined as T;

        const json = await response.json().catch(() => ({}));

        if (!response.ok) throw new Error(json.message ?? `HTTP ${response.status}`);

        return json.data as T;
    }

    private async identify(data: UserData) {
        if (!this.boot) return;
        try {
            const contact = await this.request<BootPayload['contact']>('POST', '/identify', data);
            this.boot.contact = contact;
            this.post({ type: 'contact', contact });
        } catch (error) {
            console.warn('[ChatWidget] identify failed:', (error as Error).message);
        }
    }

    private async shutdown() {
        if (!this.boot) return;
        await this.request('POST', '/shutdown').catch(() => undefined);
        this.rememberVisitor(null);
        session.set(this.sessionKey('active_trigger'), null);
        session.set(this.sessionKey('fired_triggers'), null);
        this.triggers?.stop();
        this.triggers = null;
        this.activeTrigger = null;
        this.engaged = false;
        this.clearPreviews();
        window.clearInterval(this.heartbeatTimer);
        window.removeEventListener('message', this.onFrameMessage);
        window.visualViewport?.removeEventListener('resize', this.fitToViewport);
        window.visualViewport?.removeEventListener('scroll', this.fitToViewport);
        this.lockPageScroll(false);
        this.host?.remove();
        this.boot = null;
        this.iframe = null;
        this.frameReady = false;
        this.isOpen = false;
        this.input = { ...this.input, user: undefined };
        void this.start();
    }

    private isActive() {
        return document.visibilityState === 'visible' && Date.now() - this.lastInteraction < ACTIVE_WINDOW_MS;
    }

    /**
     * Presence ping. Regular pings are throttled; page changes and presence
     * transitions (idle → active, tab hidden/shown) go out immediately.
     */
    private heartbeat(options: { page?: ReturnType<typeof currentPage>; force?: boolean } = {}) {
        if (!this.boot || this.boot.preview) return;

        const visible = document.visibilityState === 'visible';
        const elapsed = Date.now() - this.lastHeartbeat;
        const minGap = options.page
            ? 0
            : options.force
              ? MIN_FORCED_GAP_MS
              : (visible ? HEARTBEAT_MS : HIDDEN_HEARTBEAT_MS) - 1000;

        if (elapsed < minGap) return;

        this.lastHeartbeat = Date.now();
        this.request<{ cobrowse?: CobrowseSessionPayload | null }>('POST', '/heartbeat', {
            ...(options.page ? { page: options.page } : {}),
            visible,
            active: this.isActive(),
        })
            .then((data) => this.post({ type: 'cobrowse-sync', session: data?.cobrowse ?? null }))
            .catch(() => undefined);
    }

    // ---- Co-browsing --------------------------------------------------------

    private recorderPromise: Promise<NonNullable<Window['__chatCobrowse']>> | null = null;
    private cobrowseBuffer: unknown[] = [];
    private cobrowseTimer: number | undefined;

    /** The recorder (rrweb) is only downloaded when a session actually starts. */
    private loadRecorder() {
        this.recorderPromise ??= new Promise((resolve, reject) => {
            if (window.__chatCobrowse) return resolve(window.__chatCobrowse);
            const tag = document.createElement('script');
            tag.src = `${ORIGIN}/cobrowse.js`;
            tag.async = true;
            tag.onload = () =>
                window.__chatCobrowse ? resolve(window.__chatCobrowse) : reject(new Error('Recorder failed to load'));
            tag.onerror = () => {
                this.recorderPromise = null;
                reject(new Error('Recorder failed to load'));
            };
            document.head.appendChild(tag);
        });
        return this.recorderPromise;
    }

    /** Batch recorder events into ~50ms postMessages to the iframe. */
    private queueCobrowseEvent(event: unknown) {
        this.cobrowseBuffer.push(event);
        this.cobrowseTimer ??= window.setTimeout(() => {
            this.cobrowseTimer = undefined;
            const events = this.cobrowseBuffer.splice(0);
            if (events.length) this.post({ type: 'cobrowse-events', events });
        }, 50);
    }

    private onInteraction = () => {
        const wasIdle = Date.now() - this.lastInteraction >= ACTIVE_WINDOW_MS;
        this.lastInteraction = Date.now();
        if (wasIdle) this.heartbeat({ force: true });
    };

    private startHeartbeat() {
        this.heartbeatTimer = window.setInterval(() => this.heartbeat(), HEARTBEAT_MS);

        for (const event of ['pointermove', 'pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel']) {
            window.addEventListener(event, this.onInteraction, { passive: true, capture: true });
        }

        document.addEventListener('visibilitychange', () => {
            const visible = document.visibilityState === 'visible';
            if (visible) this.lastInteraction = Date.now();
            this.heartbeat({ force: true });
            this.post({ type: 'page-visible', visible });
        });
    }

    /** Record SPA navigations on the host site as page views. */
    private trackNavigation() {
        const check = () => {
            if (window.location.href === this.lastUrl) return;
            this.lastUrl = window.location.href;
            this.triggers?.evaluate();
            // Let the new page set its title first.
            window.setTimeout(() => this.heartbeat({ page: currentPage() }), 300);
        };

        for (const method of ['pushState', 'replaceState'] as const) {
            const original = history[method];
            history[method] = function (this: History, ...args: Parameters<History['pushState']>) {
                const result = original.apply(this, args);
                check();
                return result;
            };
        }
        window.addEventListener('popstate', check);
        window.addEventListener('hashchange', check);
    }

    private on(event: string, callback: Callback) {
        if (typeof callback !== 'function') return;
        (this.listeners[event] ??= new Set()).add(callback);
        if (event === 'unread') callback(this.unread);
    }

    private emit(event: string, ...args: unknown[]) {
        this.listeners[event]?.forEach((callback) => {
            try {
                callback(...args);
            } catch (error) {
                console.error(error);
            }
        });
    }
}

function init() {
    if (window.__chatWidgetLoaded) return;

    const settings = window.ChatWidgetSettings ?? {};
    if (!settings.widget_key) {
        console.warn('[ChatWidget] Missing widget_key in window.ChatWidgetSettings');
        return;
    }

    window.__chatWidgetLoaded = true;

    const queued = window.ChatWidget?.q ?? [];
    const widget = new Widget(settings);
    const api: StubFunction = (command, ...args) => widget.command(command, ...args);
    window.ChatWidget = api;

    void widget.start().then(() => queued.forEach(([command, ...args]) => widget.command(command, ...args)));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
